'use client'

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFavorites } from '@domain/marketplace/api/useFavorites'
import { useAddFavoriteItem } from '@domain/marketplace/api/useAddFavoriteItem'
import { useRemoveFavoriteItem } from '@domain/marketplace/api/useRemoveFavoriteItem'
import type { FavoriteItemDto } from '@domain/marketplace/api/favorites.types'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { useApi } from '@api/useApi.hook'
import { appConfig } from '@config'
import { QueryKey } from '@api'
import { queryClient } from '@providers/react-query'

const GUEST_FAVORITES_STORAGE_KEY = 'MARKETPLACE_GUEST_FAVORITES'

type GuestFavoriteItem = FavoriteItemDto

type MarketplaceFavoritesContextType = {
  items: FavoriteItemDto[]
  isLoading: boolean
  count: number
  isOpen: boolean
  openFavorites: () => void
  closeFavorites: () => void
  isFavorite: (contractAddress: string, nftId: number) => boolean
  toggleFavorite: (contractAddress: string, nftId: number) => Promise<void>
}

const MarketplaceFavoritesContext = createContext<
  MarketplaceFavoritesContextType | undefined
>(undefined)

export const MarketplaceFavoritesProvider = ({ children }: PropsWithChildren) => {
  const favoritesQuery = useFavorites()
  const addFavoriteItem = useAddFavoriteItem()
  const removeFavoriteItem = useRemoveFavoriteItem()
  const isAuthenticated = useIsAuthenticated()
  const { post } = useApi()
  const [isOpen, setIsOpen] = useState(false)
  const [guestItems, setGuestItems] = useState<GuestFavoriteItem[]>([])

  // Stable refs so useMemo below doesn't depend on the full mutation objects
  const addFavoriteItemRef = useRef(addFavoriteItem)
  addFavoriteItemRef.current = addFavoriteItem
  const removeFavoriteItemRef = useRef(removeFavoriteItem)
  removeFavoriteItemRef.current = removeFavoriteItem

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(GUEST_FAVORITES_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as GuestFavoriteItem[]
      if (Array.isArray(parsed)) {
        setGuestItems(parsed)
      }
    } catch {
      setGuestItems([])
    }
  }, [])
  const isSyncingRef = useRef(false)

  const syncGuestStorage = (next: GuestFavoriteItem[]) => {
    setGuestItems(next)
    try {
      window.localStorage.setItem(GUEST_FAVORITES_STORAGE_KEY, JSON.stringify(next))
    } catch {
      // best effort only
    }
  }

  useEffect(() => {
    if (!isAuthenticated || guestItems.length === 0 || isSyncingRef.current) return

    isSyncingRef.current = true

    const syncGuestFavoritesToServer = async () => {
      try {
        // Call the API directly (not via mutation) to avoid N onSuccess
        // invalidations — one single invalidation after the full loop instead
        for (const item of guestItems) {
          await post(appConfig.endpoints.v1.favorites.items, {
            disableAutoSnakeCase: true,
          })({
            contractAddress: item.contractAddress,
            nftId: Number(item.nftId),
          })
        }

        setGuestItems([])
        window.localStorage.removeItem(GUEST_FAVORITES_STORAGE_KEY)
        await queryClient.invalidateQueries({ queryKey: [QueryKey.Favorites] })
      } finally {
        isSyncingRef.current = false
      }
    }

    void syncGuestFavoritesToServer()
  }, [isAuthenticated, guestItems, post])

  const serverItems = favoritesQuery.data?.items || []
  const items = isAuthenticated ? serverItems : guestItems
  const count = items.length

  const isFavorite = useCallback(
    (contractAddress: string, nftId: number) =>
      items.some(
        (item) =>
          item.contractAddress.toLowerCase() === contractAddress.toLowerCase() &&
          Number(item.nftId) === Number(nftId),
      ),
    [items],
  )

  const toggleFavorite = useCallback(
    async (contractAddress: string, nftId: number) => {
      const normalizedAddress = contractAddress.toLowerCase()
      const matched = items.find(
        (item) =>
          item.contractAddress.toLowerCase() === normalizedAddress &&
          Number(item.nftId) === Number(nftId),
      )

      if (isAuthenticated) {
        if (matched?.id) {
          await removeFavoriteItemRef.current.mutateAsync(matched.id)
          return
        }

        await addFavoriteItemRef.current.mutateAsync({
          contractAddress,
          nftId: Number(nftId),
        })
        return
      }

      if (matched?.id) {
        syncGuestStorage(guestItems.filter((item) => item.id !== matched.id))
        return
      }

      syncGuestStorage([
        ...guestItems,
        {
          id: `${normalizedAddress}:${nftId}`,
          contractAddress,
          nftId: Number(nftId),
        },
      ])
    },
    [items, isAuthenticated, guestItems],
  )

  const value = useMemo<MarketplaceFavoritesContextType>(
    () => ({
      items,
      count,
      isOpen,
      openFavorites: () => setIsOpen(true),
      closeFavorites: () => setIsOpen(false),
      isLoading: isAuthenticated
        ? favoritesQuery.isLoading ||
          addFavoriteItem.isLoading ||
          removeFavoriteItem.isLoading
        : false,
      isFavorite,
      toggleFavorite,
    }),
    [
      items,
      isAuthenticated,
      count,
      isOpen,
      favoritesQuery.isLoading,
      addFavoriteItem.isLoading,
      removeFavoriteItem.isLoading,
      isFavorite,
      toggleFavorite,
    ],
  )

  return (
    <MarketplaceFavoritesContext.Provider value={value}>
      {children}
    </MarketplaceFavoritesContext.Provider>
  )
}

export const useMarketplaceFavorites = () => {
  const context = useContext(MarketplaceFavoritesContext)
  if (!context) {
    throw new Error(
      'useMarketplaceFavorites must be used within MarketplaceFavoritesProvider',
    )
  }
  return context
}
